import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import { getJourney, patchAnswer } from "@/lib/vg2Journey";
import { runValutaSingle } from "@/lib/vg2Valuta";
import { AbbiamoCapitoCard } from "@/components/vg2/AbbiamoCapitoCard";

const OBJECTIVE_LABELS = {
  vendere: "Vendere",
  affittare: "Affittare",
  capire_quanto_vale: "Capire quanto vale",
  generare_reddito: "Generare reddito",
  capire_quale_scelta_conviene: "Capire quale scelta conviene",
  altro: "Altro",
};
const OBJECTIVE_CHOICES = Object.entries(OBJECTIVE_LABELS).map(
  ([value, label]) => ({ value, label }));
const TYPE_CHOICES = [
  { value: "apartment", label: "Appartamento" },
  { value: "detached_house", label: "Casa indipendente / villa" },
  { value: "other_residential", label: "Altro residenziale" },
];
const CONDITION_CHOICES = [
  { value: "excellent", label: "Ottimo" },
  { value: "good", label: "Buono" },
  { value: "to_renovate", label: "Da ristrutturare" },
];
const ELEVATOR_CHOICES = [
  { value: "yes", label: "Sì" },
  { value: "no", label: "No" },
  { value: "unknown", label: "Non lo so" },
];
const EXPOSURE_CHOICES = [
  { value: "", label: "Non indicato" },
  { value: "north", label: "Nord" }, { value: "south", label: "Sud" },
  { value: "east", label: "Est" }, { value: "west", label: "Ovest" },
  { value: "double", label: "Doppia esposizione" },
];
const BRIGHTNESS_CHOICES = [
  { value: "", label: "Non indicato" },
  { value: "high", label: "Molto luminoso" },
  { value: "medium", label: "Nella media" },
  { value: "low", label: "Poco luminoso" },
];
const VIEW_CHOICES = [
  { value: "", label: "Non indicato" },
  { value: "open", label: "Aperta / panoramica" },
  { value: "standard", label: "Normale" },
  { value: "internal", label: "Su cortile o interna" },
];
const OCCUPANCY_CHOICES = [
  { value: "", label: "Non indicato" },
  { value: "free", label: "Libero" },
  { value: "owner_occupied", label: "Occupato da me / famiglia" },
  { value: "rented", label: "Locato a terzi" },
  { value: "other", label: "Altra situazione" },
];
const PERTINENZE = [
  { key: "balcony", label: "Balcone" },
  { key: "terrace", label: "Terrazzo" },
  { key: "garden", label: "Giardino" },
  { key: "cellar", label: "Cantina" },
  { key: "attic", label: "Soffitta" },
  { key: "box", label: "Box auto" },
  { key: "parking", label: "Posto auto" },
];

const MAX_WAIT_MS = 150000;
const withTimeout = (p) => Promise.race([
  p, new Promise((_, rej) => setTimeout(() => rej(new Error("__timeout__")), MAX_WAIT_MS)),
]);
const fieldCls = "w-full border border-vg2-line rounded-md px-4 py-3 text-base focus:outline-none focus:border-vg2-red focus:ring-1 focus:ring-vg2-red";
const labelCls = "block text-xs uppercase tracking-[0.12em] text-vg2-muted mb-2";

function Section({ title, subtitle, children, testid }) {
  return (
    <section data-testid={testid} className="border border-vg2-line rounded-lg p-6 bg-white">
      <h2 className="font-display text-lg mb-1">{title}</h2>
      {subtitle && <p className="text-vg2-muted text-sm mb-5">{subtitle}</p>}
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export default function ValutaRapida() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const [loaded, setLoaded] = React.useState(false);
  const [loadErr, setLoadErr] = React.useState(null);
  const [comune, setComune] = React.useState("");
  const [via, setVia] = React.useState("");
  const [civico, setCivico] = React.useState("");
  const [superficie, setSuperficie] = React.useState("");
  const [tipologia, setTipologia] = React.useState("apartment");
  const [obiettivo, setObiettivo] = React.useState("");
  const [objPreset, setObjPreset] = React.useState(false);
  // Caratteristiche facoltative
  const [rooms, setRooms] = React.useState("");
  const [bathrooms, setBathrooms] = React.useState("");
  const [floor, setFloor] = React.useState("");
  const [elevator, setElevator] = React.useState("");
  const [condition, setCondition] = React.useState("");
  const [exposure, setExposure] = React.useState("");
  const [brightness, setBrightness] = React.useState("");
  const [view, setView] = React.useState("");
  const [occupancy, setOccupancy] = React.useState("");
  const [pertSel, setPertSel] = React.useState({});     // key->true
  const [pertNone, setPertNone] = React.useState(false);
  const [pertTouched, setPertTouched] = React.useState(false);
  const [distinctive, setDistinctive] = React.useState("");  // V_free_text_notes
  const [project, setProject] = React.useState("");          // user_narrative
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [touched, setTouched] = React.useState(false);

  const [sreq, setSreq] = React.useState(null);  // V3 · richiesta confermata in «Abbiamo capito»
  const hintsApplied = React.useRef(false);

  const isApt = tipologia === "apartment" || tipologia === "other_residential";

  React.useEffect(() => {
    (async () => {
      try {
        const j = await getJourney(journeyId);
        const a = j.answers || {};
        if (a.V_analysis_id) {
          navigate(`/vg2/valuta/${journeyId}/risultato/${a.V_analysis_id}`, { replace: true });
          return;
        }
        const loc = a.A_location || {};
        if (loc.municipality) setComune(loc.municipality);
        if (loc.address) setVia(loc.address);
        if (loc.house_number) setCivico(loc.house_number);
        if (a.B_property_type) setTipologia(a.B_property_type);
        if (a.C_objective) { setObiettivo(a.C_objective); setObjPreset(true); }
        // §HOME→CAPISCI · il testo scritto in Home arriva già compilato nel
        // campo «Il tuo progetto» (modificabile). L'eventuale modifica viene
        // salvata come narrative al momento dell'invio → conservata nelle
        // fasi successive.
        if (j.initial_owner_intent_text) setProject(j.initial_owner_intent_text);
        setLoaded(true);
      } catch { setLoadErr("Non trovo la sessione. Ricomincia dall’inizio."); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  const saveAnswer = async (qid, value) => {
    try { await patchAnswer(journeyId, qid, value); } catch { /* best-effort */ }
  };

  // V3 · l'obiettivo compreso in «Abbiamo capito» sostituisce la domanda separata.
  React.useEffect(() => {
    const code = sreq?.objective_code;
    if (code && code !== obiettivo) { setObiettivo(code); saveAnswer("C_objective", code); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sreq?.objective_code]);

  // V3 · i dati dell'immobile già scritti nella richiesta precompilano solo i campi vuoti.
  React.useEffect(() => {
    const h = sreq?.property_hints;
    if (!loaded || !h || hintsApplied.current) return;
    hintsApplied.current = true;
    const fill = (cur, set, v) => { if (!cur && v != null) set(String(v)); };
    fill(comune, setComune, h.municipality); fill(via, setVia, h.address); fill(civico, setCivico, h.house_number);
    fill(superficie, setSuperficie, h.surface_sqm); fill(rooms, setRooms, h.rooms); fill(bathrooms, setBathrooms, h.bathrooms);
    fill(floor, setFloor, h.floor); fill(elevator, setElevator, h.elevator); fill(condition, setCondition, h.condition);
    fill(occupancy, setOccupancy, h.occupancy);
    if (h.property_type) setTipologia(h.property_type);
    if (h.pertinenze?.length) setPertSel(Object.fromEntries(h.pertinenze.map((k) => [k, true])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, sreq]);
  const onRequest = React.useCallback((r) => setSreq(r), []);
  const v3 = !!sreq;
  const needsClarify = v3 && !sreq.objective_code;
  const saveLocation = () => {
    if (comune.trim().length < 2 || via.trim().length < 3) return;
    const loc = { municipality: comune.trim(), address: via.trim() };
    if (civico.trim()) loc.house_number = civico.trim();
    saveAnswer("A_location", loc);
  };

  const togglePert = (key) => {
    setPertTouched(true); setPertNone(false);
    setPertSel((s) => ({ ...s, [key]: !s[key] }));
  };
  const chooseNone = () => {
    setPertTouched(true); setPertNone(true); setPertSel({});
  };

  const comuneOk = comune.trim().length >= 2;
  const viaOk = via.trim().length >= 3;
  const supNum = parseFloat(String(superficie).replace(",", "."));
  const supOk = Number.isFinite(supNum) && supNum > 0;
  const canSubmit = comuneOk && viaOk && supOk && !!tipologia && !!obiettivo && !needsClarify;

  const buildFeatures = () => {
    const f = {};
    const intOf = (v) => { const n = parseInt(String(v), 10); return Number.isFinite(n) ? n : null; };
    if (intOf(rooms) != null) f.rooms = intOf(rooms);
    if (Number.isInteger(sreq?.property_hints?.bedrooms)) f.bedrooms = sreq.property_hints.bedrooms;
    if (sreq?.property_hints?.declared_condition === "da rimodernare" && condition === "to_renovate") f.declared_condition = "da_rimodernare";
    if (intOf(bathrooms) != null) f.bathrooms = intOf(bathrooms);
    if (isApt && floor) f.floor = floor;
    if (isApt && elevator === "yes") f.elevator = true;
    else if (isApt && elevator === "no") f.elevator = false;  // "unknown"/"" → non dichiarato
    if (condition) f.condition = condition;
    if (exposure) f.exposure = exposure;
    if (brightness) f.brightness = brightness;
    if (view) f.view = view;
    if (occupancy) f.occupancy = occupancy;
    // Pertinenze: tre stati. Non toccata → nessuna chiave (NON dichiarato).
    if (pertTouched) {
      f.pertinenze_declared = true;
      PERTINENZE.forEach((p) => { f[p.key] = !!pertSel[p.key]; });
    } else {
      PERTINENZE.forEach((p) => { if (pertSel[p.key]) f[p.key] = true; });
    }
    return f;
  };

  const onSubmit = async (e) => {
    e.preventDefault(); setTouched(true); setErr(null);
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const resp = await withTimeout(runValutaSingle(journeyId, {
        municipality: comune.trim(), street: via.trim(),
        house_number: civico.trim() || null, surface_sqm: supNum,
        property_type: tipologia, objective: obiettivo,
        free_text_notes: distinctive.trim() || null,
        narrative: project.trim() || null,
        features: buildFeatures(),
      }));
      if (resp?.analysis_id) {
        navigate(`/vg2/valuta/${journeyId}/risultato/${resp.analysis_id}`); return;
      }
      setErr("Non sono riuscito a calcolare il valore. Riprova."); setBusy(false);
    } catch (e2) {
      setErr(e2?.message === "__timeout__"
        ? "La stima sta impiegando più del previsto. I dati inseriti sono salvati: riprova tra poco."
        : (e2?.response?.data?.detail?.message || "Non è stato possibile avviare la valutazione. Riprova."));
      setBusy(false);
    }
  };

  if (loadErr) return (
    <Vg2Shell><div className="pt-24" data-testid="valuta-rapida-error">
      <p role="alert" className="text-vg2-error">{loadErr}</p></div></Vg2Shell>);
  if (!loaded) return (
    <Vg2Shell><div className="pt-24 text-vg2-muted" data-testid="valuta-rapida-loading">
      Sto preparando la scheda…</div></Vg2Shell>);

  return (
    <Vg2Shell>
      <div className="pt-6 pb-24 max-w-[680px]" data-testid="valuta-rapida">
        <p className="text-vg2-muted text-xs uppercase tracking-[0.18em] mb-3">ValutaGenio · Valuta</p>
        <h1 className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-3">
          Scopri quanto vale il tuo immobile
        </h1>
        <p className="text-vg2-muted text-base leading-relaxed mb-3">
          Compila solo i campi con l’asterisco per una prima stima. Più
          caratteristiche aggiungi, più la stima diventa precisa.
        </p>
        <p className="text-vg2-muted text-xs leading-relaxed mb-8">
          Non serve registrarti né lasciare nome o email per vedere la stima.
          Alcuni testi liberi possono essere elaborati per affinare la
          valutazione; i dettagli sul trattamento sono indicati più avanti.
        </p>

        <AbbiamoCapitoCard journeyId={journeyId} onRequest={onRequest} />

        <form onSubmit={onSubmit} className="space-y-6" data-testid="valuta-rapida-form">
          {v3 && (
            <p className="text-sm text-vg2-muted" data-testid="valuta-rapida-missing-data-intro">
              Ora completa solo i dati dell’immobile che mancano: quelli già scritti nella richiesta sono precompilati e puoi correggerli.
            </p>
          )}
          {/* A · OBIETTIVO (obbligatorio, in cima) — con V3 è già in «Abbiamo capito» */}
          {!v3 && <Section testid="valuta-rapida-objective-section" title="Qual è il tuo obiettivo? *"
                   subtitle="Indica su cosa vuoi che ValutaGenio si concentri. Puoi cambiarlo quando vuoi.">
            <div className="flex flex-wrap gap-2" data-testid="valuta-rapida-objective">
              {OBJECTIVE_CHOICES.map((o) => (
                <button key={o.value} type="button"
                        data-testid={`valuta-rapida-objective-${o.value}`}
                        aria-pressed={obiettivo === o.value}
                        onClick={() => {
                          setObiettivo(o.value);
                          saveAnswer("C_objective", o.value);
                        }}
                        className={`rounded-full border px-4 py-2 text-sm transition-colors ${obiettivo === o.value ? "border-vg2-red bg-vg2-red/10 font-medium" : "border-vg2-line text-vg2-muted hover:border-vg2-red"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {objPreset && obiettivo && (
              <p className="text-xs text-vg2-muted mt-3" data-testid="valuta-rapida-objective-preset">
                Hai scelto questo obiettivo nella pagina iniziale: puoi confermarlo o cambiarlo qui.
              </p>
            )}
            {touched && !obiettivo && (
              <p className="text-vg2-error text-sm mt-3" data-testid="valuta-rapida-objective-error">
                Scegli un obiettivo per continuare.
              </p>
            )}
          </Section>}

          {/* B · DATI ESSENZIALI */}
          <Section testid="valuta-rapida-essentials" title="Dati essenziali"
                   subtitle="Obbligatori (con *). Il numero civico è facoltativo.">
            <div>
              <label htmlFor="vr_comune" className={labelCls}>Comune *</label>
              <input id="vr_comune" data-testid="valuta-rapida-comune" value={comune}
                     onChange={(e) => setComune(e.target.value)} onBlur={saveLocation}
                     placeholder="Es. Roma" autoComplete="off" className={fieldCls} />
              {touched && !comuneOk && <p className="text-vg2-error text-sm mt-2" data-testid="valuta-rapida-comune-error">Inserisci il comune.</p>}
            </div>
            <div>
              <label htmlFor="vr_via" className={labelCls}>Via o zona *</label>
              <input id="vr_via" data-testid="valuta-rapida-via" value={via}
                     onChange={(e) => setVia(e.target.value)} onBlur={saveLocation}
                     placeholder="Es. Via Raffaele Paolucci" autoComplete="off" className={fieldCls} />
              {touched && !viaOk && <p className="text-vg2-error text-sm mt-2" data-testid="valuta-rapida-via-error">Inserisci la via o la zona dell’immobile.</p>}
              {/^\s*(zona|quartiere|rione)\s+/i.test(via) && (
                <p className="text-xs text-vg2-muted mt-2" data-testid="valuta-rapida-via-zone-note">
                  Hai indicato la zona: la stima userà la zona. Se scrivi anche la via (es. «Via Murri 12»), sarà più precisa.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="vr_civico" className={labelCls}>Numero civico (facoltativo)</label>
              <input id="vr_civico" data-testid="valuta-rapida-civico" value={civico}
                     onChange={(e) => setCivico(e.target.value)} onBlur={saveLocation}
                     placeholder="Es. 13" autoComplete="off" className={fieldCls} />
              <p className="text-xs text-vg2-muted mt-2">Puoi lasciarlo vuoto, anche per privacy: useremo la migliore localizzazione affidabile.</p>
            </div>
            <div>
              <label htmlFor="vr_superficie" className={labelCls}>Superficie (m²) *</label>
              <input id="vr_superficie" data-testid="valuta-rapida-superficie" value={superficie}
                     inputMode="numeric" onChange={(e) => setSuperficie(e.target.value)}
                     placeholder="Es. 90" autoComplete="off" className={fieldCls} />
              {touched && !supOk && <p className="text-vg2-error text-sm mt-2" data-testid="valuta-rapida-superficie-error">Inserisci la superficie in metri quadri.</p>}
            </div>
            <div>
              <label className={labelCls}>Tipologia *</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="valuta-rapida-tipologia">
                {TYPE_CHOICES.map((t) => (
                  <button key={t.value} type="button" data-testid={`valuta-rapida-tipologia-${t.value}`}
                          onClick={() => { setTipologia(t.value); saveAnswer("B_property_type", t.value); }}
                          className={`rounded-md border px-3 py-3 text-sm text-left transition-colors ${tipologia === t.value ? "border-vg2-red bg-vg2-red/5 font-medium" : "border-vg2-line text-vg2-muted hover:border-vg2-red"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          {/* B · CARATTERISTICHE (facoltative) */}
          <Section testid="valuta-rapida-characteristics" title="Caratteristiche"
                   subtitle="Tutte facoltative. Ciò che non indichi resta “non dichiarato” e non penalizza la stima.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="vr_rooms" className={labelCls}>Numero di locali</label>
                <input id="vr_rooms" data-testid="valuta-rapida-rooms" value={rooms} inputMode="numeric"
                       onChange={(e) => setRooms(e.target.value)} placeholder="Esclusi bagni e cucina" className={fieldCls} />
              </div>
              <div>
                <label htmlFor="vr_bathrooms" className={labelCls}>Numero di bagni</label>
                <input id="vr_bathrooms" data-testid="valuta-rapida-bathrooms" value={bathrooms} inputMode="numeric"
                       onChange={(e) => setBathrooms(e.target.value)} placeholder="Es. 2" className={fieldCls} />
              </div>
              {isApt && (
                <div>
                  <label htmlFor="vr_floor" className={labelCls}>Piano</label>
                  <select id="vr_floor" data-testid="valuta-rapida-floor" value={floor}
                          onChange={(e) => setFloor(e.target.value)} className={fieldCls}>
                    <option value="">Non indicato</option>
                    <option value="basement">Seminterrato</option>
                    <option value="ground">Piano terra</option>
                    <option value="first">Primo piano</option>
                    <option value="intermediate">Piano intermedio</option>
                    <option value="top">Ultimo piano / attico</option>
                    <option value="multi_level">Su più livelli</option>
                  </select>
                </div>
              )}
              {isApt && (
                <div>
                  <label htmlFor="vr_elevator" className={labelCls}>Ascensore</label>
                  <select id="vr_elevator" data-testid="valuta-rapida-elevator" value={elevator}
                          onChange={(e) => setElevator(e.target.value)} className={fieldCls}>
                    <option value="">Non indicato</option>
                    {ELEVATOR_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label htmlFor="vr_condition" className={labelCls}>Stato conservativo</label>
                <select id="vr_condition" data-testid="valuta-rapida-condition" value={condition}
                        onChange={(e) => setCondition(e.target.value)} className={fieldCls}>
                  <option value="">Non indicato</option>
                  {CONDITION_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                {sreq?.property_hints?.declared_condition && condition === "to_renovate" && (
                  <p className="text-xs text-vg2-muted mt-1" data-testid="valuta-rapida-declared-condition-note">
                    Hai indicato «{sreq.property_hints.declared_condition}»: il valore attuale è calcolato come «da ristrutturare», i costi e i tempi dei lavori come rimodernamento.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="vr_occupancy" className={labelCls}>Stato di occupazione</label>
                <select id="vr_occupancy" data-testid="valuta-rapida-occupancy" value={occupancy}
                        onChange={(e) => setOccupancy(e.target.value)} className={fieldCls}>
                  {OCCUPANCY_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Qualità abitativa */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <label htmlFor="vr_exposure" className={labelCls}>Esposizione</label>
                <select id="vr_exposure" data-testid="valuta-rapida-exposure" value={exposure}
                        onChange={(e) => setExposure(e.target.value)} className={fieldCls}>
                  {EXPOSURE_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="vr_brightness" className={labelCls}>Luminosità</label>
                <select id="vr_brightness" data-testid="valuta-rapida-brightness" value={brightness}
                        onChange={(e) => setBrightness(e.target.value)} className={fieldCls}>
                  {BRIGHTNESS_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="vr_view" className={labelCls}>Vista</label>
                <select id="vr_view" data-testid="valuta-rapida-view" value={view}
                        onChange={(e) => setView(e.target.value)} className={fieldCls}>
                  {VIEW_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Pertinenze: selezione multipla, 3 stati */}
            <div data-testid="valuta-rapida-pertinenze">
              <label className={labelCls}>Pertinenze e dotazioni</label>
              <p className="text-xs text-vg2-muted mb-3">
                Indica solo ciò che è presente. Se non compili questa sezione
                resta “non dichiarata” (neutra). Usa “Nessuna pertinenza” se
                non ne hai.
              </p>
              <div className="flex flex-wrap gap-2">
                {PERTINENZE.map((p) => (
                  <button key={p.key} type="button" data-testid={`valuta-rapida-pert-${p.key}`}
                          onClick={() => togglePert(p.key)}
                          className={`rounded-full border px-4 py-2 text-sm transition-colors ${pertSel[p.key] ? "border-vg2-red bg-vg2-red/10 font-medium" : "border-vg2-line text-vg2-muted hover:border-vg2-red"}`}>
                    {p.label}
                  </button>
                ))}
                <button type="button" data-testid="valuta-rapida-pert-none" onClick={chooseNone}
                        className={`rounded-full border px-4 py-2 text-sm transition-colors ${pertNone ? "border-vg2-ink bg-vg2-ink/5 font-medium" : "border-vg2-line text-vg2-muted hover:border-vg2-ink"}`}>
                  Nessuna pertinenza
                </button>
              </div>
              {pertTouched && (
                <p className="text-xs text-vg2-muted mt-2" data-testid="valuta-rapida-pert-state">
                  Sezione dichiarata: le voci non selezionate risultano assenti (nessun premio, nessuna penalità).
                </p>
              )}
            </div>
          </Section>

          {/* C · CARATTERISTICHE DISTINTIVE (V_free_text_notes) */}
          <Section testid="valuta-rapida-distinctive" title="Caratteristiche distintive"
                   subtitle="Facoltativo.">
            <textarea id="vr_distinctive" data-testid="valuta-rapida-distinctive-input"
                      value={distinctive} rows={3} maxLength={500}
                      onChange={(e) => setDistinctive(e.target.value)}
                      placeholder="Ci sono caratteristiche particolari che rendono unico il tuo immobile? Es. vista mare, panorama, luminosità eccezionale, finiture di pregio, interventi migliorativi."
                      className={`${fieldCls} resize-y`} />
          </Section>

          {/* E · PROGETTO PERSONALE (user_narrative) */}
          {!v3 && <Section testid="valuta-rapida-project" title="Il tuo progetto" subtitle="Facoltativo.">
            <textarea id="vr_project" data-testid="valuta-rapida-project-input"
                      value={project} rows={3} maxLength={2000}
                      onChange={(e) => setProject(e.target.value)}
                      placeholder="Se tutto andasse come desideri, cosa dovrebbe permetterti di fare questo immobile nei prossimi mesi o anni?"
                      className={`${fieldCls} resize-y`} />
          </Section>}

          {touched && needsClarify && (
            <p role="alert" data-testid="valuta-rapida-clarify-error" className="text-sm text-vg2-error">
              Indica in «Abbiamo capito» quali operazioni vuoi valutare per continuare.
            </p>
          )}
          {err && <p role="alert" data-testid="valuta-rapida-submit-error" className="text-sm text-vg2-error">{err}</p>}

          <div className="pt-1">
            <button type="submit" data-testid="valuta-rapida-submit" disabled={busy}
                    className="w-full sm:w-auto rounded-full bg-vg2-red text-white hover:bg-vg2-red-hover px-10 py-4 text-base font-semibold disabled:opacity-50 transition-colors">
              {busy ? "Calcolo il valore…" : "Scopri il valore →"}
            </button>
          </div>
        </form>
      </div>
    </Vg2Shell>
  );
}
